import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useAuthStore } from "@/features/auth/stores/authStore";

type ControlsStore = {
  areTipsVisible: boolean;
  isSpeedMapVisible: boolean;
  areFlucsVisible: boolean;
  forceSp: boolean;
  sortBy: "runner" | "odds" | "barrier";
  sortDirection: "asc" | "desc";
};

type ControlsActions = {
  setDirection: (sortDirection: ControlsStore["sortDirection"]) => void;
  setSortBy: (sortBy: ControlsStore["sortBy"]) => void;
  toggleSortBy: (sortBy: ControlsStore["sortBy"]) => void;
  setAreTipsVisible: (areTipsVisible: boolean) => void;
  setAreFlucsVisible: (areFlucsVisible: boolean) => void;
  setIsSpeedMapVisible: (isSpeedMapVisible: boolean) => void;
  setForceSp: (forceSp: boolean) => void;
};

type ControlsState = ControlsStore & ControlsActions;

const getUserKey = () => {
  const state = useAuthStore.getState();
  return state.session?.userId ?? "guest";
};

const initialState: ControlsStore = {
  areTipsVisible: false,
  isSpeedMapVisible: false,
  areFlucsVisible: false,
  forceSp: false,
  sortBy: "runner",
  sortDirection: "asc",
};

const createControlsStore = (name: string) =>
  create<ControlsState>()(
    persist(
      (set) => ({
        ...initialState,
        setForceSp: (forceSp) => set({ forceSp }),
        setAreFlucsVisible: (areFlucsVisible) => set({ areFlucsVisible }),
        setIsSpeedMapVisible: (isSpeedMapVisible) => set({ isSpeedMapVisible }),
        setAreTipsVisible: (areTipsVisible) => set({ areTipsVisible }),
        setDirection: (sortDirection) => set({ sortDirection }),
        setSortBy: (sortBy) => set({ sortBy }),
        toggleSortBy: (sortBy) =>
          set((state) => {
            if (state.sortBy === sortBy) {
              // need to toggle direction instead
              return {
                ...state,
                sortDirection: state.sortDirection === "asc" ? "desc" : "asc",
              };
            }

            return { ...state, sortBy };
          }),
        reset: () => set(initialState),
      }),
      { name },
    ),
  );

let raceStore: any = null;
let pickemsStore: any = null;
let lastUserKey: any = null;

const useRaceControlsStore = (selector?: any) => {
  const key = getUserKey();

  if (lastUserKey !== key) {
    raceStore = null;
    pickemsStore = null;
    lastUserKey = key;
  }

  if (!raceStore) {
    raceStore = createControlsStore(`race-controls-${key}`);
  }

  return selector ? raceStore(selector) : raceStore();
};

const usePickemsControlsStore = (selector?: any) => {
  const key = getUserKey();

  if (lastUserKey !== key) {
    raceStore = null;
    pickemsStore = null;
    lastUserKey = key;
  }

  if (!pickemsStore) {
    pickemsStore = createControlsStore(`pickems-controls-${key}`);
  }

  return selector ? pickemsStore(selector) : pickemsStore();
};

const useControlsStore = (isPickems?: boolean) => {
  const raceState = useRaceControlsStore();
  const pickemsState = usePickemsControlsStore();

  return !isPickems ? raceState : pickemsState;
};

const useControlsStoreSelector = <T>(
  selector: (state: ControlsState) => T,
  isPickems: boolean = false,
): T => {
  const raceState = useRaceControlsStore(selector);
  const pickemsState = usePickemsControlsStore(selector);

  return !isPickems ? raceState : pickemsState;
};

export {
  useRaceControlsStore,
  usePickemsControlsStore,
  useControlsStore,
  useControlsStoreSelector,
};

export type { ControlsStore };
